import apolloLogger from 'apollo-link-logger';
import {
  ApolloLink,
  InMemoryCache,
  ApolloClient,
  gql,
  split,
} from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  httpLink,
  authLink,
  errorLink,
  wsLink,
} from 'services/apollo-client/links';
import storage from 'services/storage';

const nonTerminatingLinks = ApolloLink.from([apolloLogger, errorLink]);

const splitNetworkLink = split(
  ({ query, operationName, setContext }) => {
    const definition = getMainDefinition(query);

    const WSOperations = [
      'surfaceNodeMovedOnSurface',
      'collaboratingUserCursorMovedOnSurface',
    ];

    const shouldGoThroughWS = WSOperations.includes(operationName);

    return (
      shouldGoThroughWS ||
      (definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription')
    );
  },
  wsLink,
  httpLink as any,
);

const networkLink = authLink.concat(splitNetworkLink as any);

export const NODE_TYPE_MAP = {
  TEXT: 'SurfaceTextNode',
  NOTE: 'SurfaceNoteNode',
  DOCUMENT: 'SurfaceDocumentNode',
  PROTOCOL: 'SurfaceProtocolNode',
  SUB_SURFACE: 'SubSurfaceNode',
  IMAGE: 'SurfaceImageNode',
  BACKGROUND: 'SurfaceBackgroundNode',
  QUESTIONNAIRE: 'SurfaceQuestionnaireNode',
  DOCUMENT_SNIPPET: 'SurfaceDocumentNodeSnippet',
  TEXT_SNIPPET: 'SurfaceTextNodeSnippet',
};

const typeDefs = gql`
  type Stage {
    id: String
    scale: Float
    x: Float
    y: Float
  }

  extend type Query {
    stages: [Stage]
  }
`;

export const cache = new InMemoryCache({
  possibleTypes: {
    SurfaceNodeUnion: Object.values(NODE_TYPE_MAP),
  },

  typePolicies: {
    Query: {
      fields: {
        stages: {
          read(stages = []) {
            return stages;
          },
          merge(oldValue, newValue) {
            return newValue;
          },
        },
      },
    },
    User: {
      fields: {
        token: {
          read() {
            return storage.getToken();
          },
        },
      },
    },
    SurfaceNodePointsTo: {
      keyFields: (object) => {
        //@ts-ignore
        return `points-to-${object.from.id + object.to.id}`;
      },
    },
    RisSearchItem: {
      keyFields: () => undefined,
    },
  },
});

const links = [nonTerminatingLinks, networkLink];

const apolloClient = new ApolloClient({
  link: ApolloLink.from(links as any),
  cache,
  typeDefs,
});

export function clearStore() {
  apolloClient.clearStore().then(() => {
    apolloClient.resetStore();
    apolloClient.cache.reset();
    //@ts-ignore
    wsLink.subscriptionClient.close();
  });
}

export function isAuthenticationError(error) {
  return (error?.extensions?.code ?? error?.code)?.includes('UNAUTHENTICATED');
}

export function isAuthorizationError(error) {
  return (error?.extensions?.code ?? error?.code)?.includes('UNAUTHORIZED');
}

export function isAuthError(error) {
  return isAuthenticationError(error) || isAuthorizationError(error);
}

export { onError } from 'services/apollo-client/links/error-link';

export default apolloClient;
